import * as React from 'react';

interface LottiePlayerProps {
  animationData?: string;
  loop?: boolean;
  autoplay?: boolean;
  onLoopComplete?: () => void;
  onComplete?: () => void;
  clickToPauseDisabled?: boolean;
  paused?: boolean;
  style?: React.CSSProperties;
}

const LottiePlayer = ({
  animationData,
  loop = true,
  autoplay = true,
  onLoopComplete,
  onComplete,
  clickToPauseDisabled,
  paused,
  style,
}: LottiePlayerProps) => {
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    if (!containerRef.current) return;
    if (window.lottie) {
      const animation = window.lottie.loadAnimation({
        container: containerRef.current,
        renderer: 'svg',
        loop,
        autoplay,
        animationData,
      });

      onLoopComplete &&
        animation.addEventListener('loopComplete', onLoopComplete);
      const handleAnimationComplete = () => {
        onComplete && onComplete();
        if (!loop) {
          animation.goToAndStop(animation.totalFrames - 1, true);
          animation.pause();
        }
      };

      animation.addEventListener('complete', handleAnimationComplete);

      if (paused) {
        animation.pause();
      } else animation.play();

      return () => {
        animation.removeEventListener('complete', handleAnimationComplete);
        onLoopComplete &&
          animation.removeEventListener('loopComplete', onLoopComplete);
        animation.destroy();
      };
    } else {
      console.error('Lottie is not available in window');
      return;
    }
  }, [
    animationData,
    loop,
    autoplay,
    paused,
    onLoopComplete,
    onComplete,
    containerRef,
  ]);

  if (!animationData) return null;

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (clickToPauseDisabled) e.preventDefault();
  };

  return (
    <div
      data-testid="lottie-player"
      onClick={handleClick}
      ref={containerRef}
      style={style}
    ></div>
  );
};

export default LottiePlayer;
